import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { AdminScheduleList, ScheduleBody, ScheduleView } from '@wodi/shared';
import {
  Button,
  Cell,
  Column,
  DateField,
  Dialog,
  DialogActions,
  EmptyState,
  LoadingBlock,
  Notice,
  RadioGroup,
  Row,
  Select,
  Table,
  TableBody,
  TableHeader,
  Tag,
  TextField,
} from '@wodi/ui';
import { useState } from 'react';

import {
  adminSchedulesQuery,
  appointmentKeys,
  createSchedule,
  updateSchedule,
} from '../api/appointments.ts';
import styles from '../appointments/appointments.module.css';
import { sessionsInWords, WEEKDAYS } from '../appointments/labels.ts';
import { todayIso } from '../appointments/booking-dialog.tsx';
import { useOnline } from '../hooks/use-online.ts';
import { TableScroll } from '../layout/screen.tsx';
import { errorText } from '../stock/common.tsx';
import { toasts } from '../toasts.ts';
import { SettingsPage } from './settings-nav.tsx';

// Settings > Appointments (docs/plans/phase2-appointments.md §6): the schedules patients
// can be booked into at this facility.

interface SessionDraft {
  key: string;
  weekday: string;
  start: string;
  end: string;
}

const NO_PRACTITIONER = 'none';

interface Draft {
  name: string;
  servicePointId: string | null;
  practitionerUserId: string;
  slotMinutes: string;
  slotCapacity: string;
  validFrom: string;
  validUntil: string | null;
  status: 'active' | 'inactive';
  sessions: SessionDraft[];
}

function draftFrom(schedule: ScheduleView | null): Draft {
  return {
    name: schedule?.name ?? '',
    servicePointId: schedule?.servicePoint.id ?? null,
    practitionerUserId: schedule?.practitioner?.userId ?? NO_PRACTITIONER,
    slotMinutes: String(schedule?.slotMinutes ?? 15),
    slotCapacity: String(schedule?.slotCapacity ?? 1),
    validFrom: schedule?.validFrom ?? todayIso(),
    validUntil: schedule?.validUntil ?? null,
    status: schedule?.status ?? 'active',
    sessions: (
      schedule?.weeklySessions ??
      [1, 2, 3, 4, 5].map((w) => ({ weekday: w, start: '08:00', end: '12:00' }))
    ).map((s) => ({
      key: crypto.randomUUID(),
      weekday: String(s.weekday),
      start: s.start,
      end: s.end,
    })),
  };
}

function ScheduleDialog({
  schedule,
  options,
  onClose,
}: {
  schedule: ScheduleView | null;
  options: AdminScheduleList;
  onClose: () => void;
}) {
  const online = useOnline();
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState(() => draftFrom(schedule));
  const [tried, setTried] = useState(false);
  const set = <K extends keyof typeof draft>(key: K, value: (typeof draft)[K]) => {
    setDraft((d) => ({ ...d, [key]: value }));
  };
  const setSession = (key: string, patch: Partial<SessionDraft>) => {
    setDraft((d) => ({
      ...d,
      sessions: d.sessions.map((s) => (s.key === key ? { ...s, ...patch } : s)),
    }));
  };

  const body = (): ScheduleBody | null => {
    const slotMinutes = Number(draft.slotMinutes);
    const slotCapacity = Number(draft.slotCapacity);
    if (
      draft.name.trim() === '' ||
      draft.servicePointId === null ||
      !Number.isInteger(slotMinutes) ||
      !Number.isInteger(slotCapacity) ||
      draft.sessions.length === 0
    ) {
      return null;
    }
    return {
      name: draft.name.trim(),
      servicePointId: draft.servicePointId,
      practitionerUserId:
        draft.practitionerUserId === NO_PRACTITIONER ? null : draft.practitionerUserId,
      slotMinutes,
      slotCapacity,
      weeklySessions: draft.sessions.map((s) => ({
        weekday: Number(s.weekday),
        start: s.start.trim(),
        end: s.end.trim(),
      })),
      validFrom: draft.validFrom,
      validUntil: draft.validUntil,
      status: draft.status === 'inactive' ? 'inactive' : 'active',
    };
  };

  const save = useMutation({
    mutationFn: (b: ScheduleBody) =>
      schedule === null ? createSchedule(b) : updateSchedule(schedule.id, b),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: appointmentKeys.schedules });
      toasts.show({ title: schedule === null ? 'Schedule added' : 'Schedule saved' });
      onClose();
    },
  });

  const submit = () => {
    setTried(true);
    const b = body();
    if (b !== null && online) save.mutate(b);
  };

  return (
    <Dialog
      title={schedule === null ? 'Add schedule' : 'Edit schedule'}
      isOpen
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <form
        className={styles.form}
        onSubmit={(event) => {
          event.preventDefault();
          submit();
        }}
      >
        <TextField
          label="Name"
          description="What reception sees when booking, for example General OPD mornings."
          value={draft.name}
          onChange={(v) => {
            set('name', v);
          }}
          maxLength={80}
          {...(tried && draft.name.trim() === '' ? { errorMessage: 'Name the schedule.' } : {})}
        />
        <div className={styles.row}>
          <Select
            label="Clinic"
            description="The queue the patient joins at check-in."
            options={options.clinics.map((c) => ({ id: c.id, label: c.name }))}
            value={draft.servicePointId}
            onChange={(v) => {
              set('servicePointId', String(v));
            }}
            {...(tried && draft.servicePointId === null
              ? { errorMessage: 'Choose the clinic.' }
              : {})}
          />
          <Select
            label="Practitioner"
            options={[
              { id: NO_PRACTITIONER, label: 'Anyone in the clinic' },
              ...options.practitioners.map((p) => ({ id: p.userId, label: p.name })),
            ]}
            value={draft.practitionerUserId}
            onChange={(v) => {
              set('practitionerUserId', String(v));
            }}
          />
        </div>
        <div className={styles.row}>
          <TextField
            label="Slot length"
            unit="minutes"
            inputMode="numeric"
            width="sm"
            value={draft.slotMinutes}
            onChange={(v) => {
              set('slotMinutes', v);
            }}
          />
          <TextField
            label="Patients per slot"
            inputMode="numeric"
            width="sm"
            value={draft.slotCapacity}
            onChange={(v) => {
              set('slotCapacity', v);
            }}
          />
          <DateField
            label="First day"
            value={draft.validFrom}
            onChange={(v) => {
              if (v !== null) set('validFrom', v);
            }}
          />
          <DateField
            label="Last day (optional)"
            value={draft.validUntil}
            onChange={(v) => {
              set('validUntil', v);
            }}
          />
        </div>
        <fieldset className={styles.form}>
          <legend>Weekly sessions</legend>
          {draft.sessions.map((s, i) => (
            <div key={s.key} className={styles.session}>
              <Select
                label={`Day ${String(i + 1)}`}
                options={WEEKDAYS}
                value={s.weekday}
                onChange={(v) => {
                  setSession(s.key, { weekday: String(v) });
                }}
              />
              <TextField
                label="From"
                width="sm"
                value={s.start}
                onChange={(v) => {
                  setSession(s.key, { start: v });
                }}
              />
              <TextField
                label="To"
                width="sm"
                value={s.end}
                onChange={(v) => {
                  setSession(s.key, { end: v });
                }}
              />
              <Button
                dense
                variant="quiet"
                onPress={() => {
                  setDraft((d) => ({ ...d, sessions: d.sessions.filter((x) => x.key !== s.key) }));
                }}
              >
                Remove
              </Button>
            </div>
          ))}
          <div>
            <Button
              dense
              onPress={() => {
                setDraft((d) => ({
                  ...d,
                  sessions: [
                    ...d.sessions,
                    { key: crypto.randomUUID(), weekday: '1', start: '08:00', end: '12:00' },
                  ],
                }));
              }}
            >
              Add session
            </Button>
          </div>
          <p className={styles.meta}>Times are 24-hour, East Africa Time, in 5-minute steps.</p>
        </fieldset>
        <RadioGroup
          label="Status"
          orientation="horizontal"
          value={draft.status}
          onChange={(v) => {
            set('status', v === 'inactive' ? 'inactive' : 'active');
          }}
          options={[
            { id: 'active', label: 'Open for booking' },
            { id: 'inactive', label: 'Switched off' },
          ]}
        />
        {save.isError ? (
          <Notice tone="error">
            {errorText(save.error, "The schedule couldn't be saved. Try again.")}
          </Notice>
        ) : null}
        {tried && body() === null ? (
          <Notice tone="info">
            Fill in the name, clinic, slot length, patients per slot and at least one session.
          </Notice>
        ) : null}
        <DialogActions>
          <Button variant="quiet" onPress={onClose}>
            Close
          </Button>
          <Button
            type="submit"
            variant="primary"
            isDisabled={!online}
            isPending={save.isPending}
            pendingLabel="Saving…"
          >
            {schedule === null ? 'Add schedule' : 'Save schedule'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}

export function AppointmentSettingsPage() {
  const list = useQuery(adminSchedulesQuery);
  const online = useOnline();
  const [editing, setEditing] = useState<ScheduleView | 'new' | null>(null);
  return (
    <SettingsPage
      title="Appointments"
      panel
      actions={
        <Button
          variant="primary"
          isDisabled={!online || !list.isSuccess}
          onPress={() => {
            setEditing('new');
          }}
        >
          Add schedule
        </Button>
      }
    >
      {list.isPending ? <LoadingBlock label="Loading schedules" rows={3} /> : null}
      {list.isError ? (
        <Notice
          tone="error"
          action={
            <Button dense onPress={() => void list.refetch()}>
              Try again
            </Button>
          }
        >
          {errorText(list.error, "The schedules couldn't be loaded. Try again.")}
        </Notice>
      ) : null}
      {list.isSuccess && list.data.items.length === 0 ? (
        <EmptyState>
          No schedules yet. Add one so reception can book appointments at this facility.
        </EmptyState>
      ) : null}
      {list.isSuccess && list.data.items.length > 0 ? (
        <TableScroll>
          <Table aria-label="Appointment schedules" flat>
            <TableHeader>
              <Column isRowHeader>Name</Column>
              <Column>Clinic</Column>
              <Column>Practitioner</Column>
              <Column>Slots</Column>
              <Column>Sessions</Column>
              <Column>Status</Column>
              <Column>
                <span className={styles.meta}>Action</span>
              </Column>
            </TableHeader>
            <TableBody items={list.data.items}>
              {(s) => (
                <Row id={s.id}>
                  <Cell>{s.name}</Cell>
                  <Cell>{s.servicePoint.name}</Cell>
                  <Cell>{s.practitioner?.name ?? 'Anyone'}</Cell>
                  <Cell>{`${String(s.slotMinutes)} min, ${String(s.slotCapacity)} per slot`}</Cell>
                  <Cell>
                    <span className={styles.meta}>{sessionsInWords(s.weeklySessions)}</span>
                  </Cell>
                  <Cell>
                    <Tag state={s.status === 'active' ? 'active' : 'neutral'}>
                      {s.status === 'active' ? 'Open for booking' : 'Switched off'}
                    </Tag>
                  </Cell>
                  <Cell>
                    <Button
                      dense
                      isDisabled={!online}
                      onPress={() => {
                        setEditing(s);
                      }}
                    >
                      Edit
                    </Button>
                  </Cell>
                </Row>
              )}
            </TableBody>
          </Table>
        </TableScroll>
      ) : null}
      {editing === null || !list.isSuccess ? null : (
        <ScheduleDialog
          schedule={editing === 'new' ? null : editing}
          options={list.data}
          onClose={() => {
            setEditing(null);
          }}
        />
      )}
    </SettingsPage>
  );
}
